// ABOUTME: SPEC-82 D-1 — the one reader for `/api/config.ui_experiment`: a frozen {name: true} map.
// ABOUTME: Tolerant by design (the backend has no allowlist), so a bad entry reads as OFF, never as a crash.

// The ten frontend names (SPEC-82 §0). Constants so no component spells a toggle twice; an unknown
// name in the config is still passed through (the backend does the same) and simply read by nobody.
export const TOGGLE = Object.freeze({
  SKIP_INTRO_FILM: 'skip_intro_film',
  TAP_TO_TALK: 'tap_to_talk',
  GLOW_RING: 'glow_ring',
  BARE_FOOTER: 'bare_footer',
  CONTROL_REVEAL: 'control_reveal',
  HIDE_VIEWS: 'hide_views',
  COLLECTION_MENU: 'collection_menu',
  BAG_TOP_RIGHT: 'bag_top_right',
  VOICE_HINT: 'voice_hint',
  PRIMER_COLLAPSIBLE: 'primer_collapsible',
  // CPO friction experiment (2026-10-08, dev-ophion only): no Enter Boutique, the mic CTA is the entry; phone CTA label to its right.
  NO_ENTER_GATE: 'no_enter_gate',
  TALK_LABEL_RIGHT: 'talk_label_right',
  // Owner 2026-10-08 (dev + staging, never prod): the mic button is the one voice control — no voice row in the privacy panel,
  // no muted-concierge widget, one tap on the mic turns voice on; "off for good" is the browser's site setting.
  MIC_VOICE_CONTROL: 'mic_voice_control',
  // CPO go-live (2026-10-10): under no_enter_gate a first-timer gets the intro film first (muted until a tap, no splash, no mic),
  // then the same 'Talk to …' landing skip_intro_film gives them.
  FIRST_VISIT_FILM: 'first_visit_film',
});

// Shared by every caller with no experiment: identity-stable so a useMemo/prop comparison never
// sees a new object for "off".
export const EMPTY = Object.freeze({});

// Mirrors the backend's name rule (SPEC-81 §4.7): lower-case snake. Anything else is malformed.
const NAME_RE = /^[a-z][a-z0-9_]*$/;

// Dev bisect hatch: localStorage `versai_ui_experiment_off` = "glow_ring,bare_footer" forces those toggles OFF after a reload, so a
// lag can be bisected without restarting the backend. It can only SUBTRACT (never turns anything on); unset = today's behaviour exactly.
export const FORCE_OFF_KEY = 'versai_ui_experiment_off';
function forcedOff() {
  try {
    const s = window.localStorage.getItem(FORCE_OFF_KEY);
    return new Set(s ? s.split(',').map((n) => n.trim()).filter(Boolean) : []);
  } catch { return new Set(); }
}

/**
 * @param {*} raw the value of `brandConfig.ui_experiment` (any shape; usually absent)
 * @returns {Readonly<Record<string, true>>} only well-formed names whose value is exactly `true`
 */
export function readUiExperiment(raw) {
  try {
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return EMPTY;
    const off = forcedOff();
    const out = {};
    for (const [name, value] of Object.entries(raw)) {
      if (value === true && NAME_RE.test(name) && !off.has(name)) out[name] = true;
    }
    return Object.keys(out).length ? Object.freeze(out) : EMPTY;
  } catch {
    // A hostile getter/proxy must not take boot down: off is always a valid state.
    return EMPTY;
  }
}
